import React, { FC, InputHTMLAttributes, useRef, useState } from 'react';
import { cn } from '@/shared/lib/utils';

export interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number;
  onChange: (val: number) => void;
  suffix?: string;
  isManual?: boolean;
}

// SAFE-02: seeds/resyncs the buffer from the numeric prop. Encodes the
// "zero plus placeholder means empty" rule exactly once — a buffer the user has
// never typed into renders empty (and the placeholder shows through); a buffer
// the user typed a literal zero into is a different string ("0") and is untouched
// by this helper, since it only ever runs at seed time / on an unfocused resync.
const seedBuffer = (value: number, placeholder?: string): string =>
  value === 0 && placeholder ? '' : String(value);

export const NumberInput: FC<NumberInputProps> = ({
  value,
  onChange,
  suffix,
  isManual = false,
  className,
  placeholder,
  onFocus,
  onBlur,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [buffer, setBuffer] = useState<string>(() => seedBuffer(value, placeholder));
  // SAFE-02: tracks whether this input currently has focus, so the resync guard
  // below can tell a genuine external value change apart from the normal
  // controlled-component round-trip (the prop echoing back what we just emitted).
  // Kept as state rather than a ref (a plain `useRef` would be read during
  // render by the guard below, which this repo's `eslint-plugin-react-hooks`
  // "refs" rule forbids) — this also matches RoomInput.tsx's own precedent,
  // which tracks its analogous `activeField` as state, not a ref, for the same
  // reason.
  const [isFocused, setIsFocused] = useState(false);

  // SAFE-02 resync rule — mirrors src/widgets/editor/RoomInput.tsx's
  // activeField-gated localDims resync: state is adjusted directly during
  // render, by comparing against the previous prop values (RoomInput's own
  // `prevProps` pattern), rather than inside a useEffect — calling setState
  // synchronously from an effect is disallowed by this repo's lint config
  // (`react-hooks/set-state-in-effect`) and would also add an extra
  // post-commit render pass for no benefit here. Only overwrite the buffer
  // from the incoming prop when this input is not currently focused, collapsed
  // to a single boolean since this component owns exactly one field. While
  // focused the buffer is authoritative: the prop re-arriving with the value
  // this input itself just emitted is not an external change, and resyncing
  // from it is exactly what corrupts mid-typing decimal entry.
  const [prevSeed, setPrevSeed] = useState({ value, placeholder });
  if (prevSeed.value !== value || prevSeed.placeholder !== placeholder) {
    setPrevSeed({ value, placeholder });
    if (!isFocused) {
      setBuffer(seedBuffer(value, placeholder));
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/,/g, '.');
    // A second separator is ignored outright — buffer and onChange both untouched.
    if ((raw.match(/\./g) || []).length > 1) return;
    setBuffer(raw);
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
      onChange(0);
      return;
    }
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
  };

  // SAFE-02 focus-handler precedence: preserves today's exact semantics — a
  // caller-supplied onFocus REPLACES the internal select-on-focus rather than
  // supplementing it (only src/widgets/editor/RoomInput.tsx's three fields
  // supply their own, so composing both would silently give them a
  // select-on-focus behaviour they have never had — an unrequested UX change).
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    } else {
      e.target.select();
    }
  };

  // WR-01 (Phase 34 review): the render-time resync guard above cannot cover this case —
  // a blur alone changes no prop, so the component never re-renders on the way out and the
  // guard's `if` never re-enters; and by the time focus is lost, `prevSeed` may already equal
  // the current `value` (advanced during an earlier render while still focused), so the guard
  // would not fire even if a later render arrived. Reconciling here, unconditionally, on the
  // way out of the field closes that gap: the buffer can never outlive the authoritative prop.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setBuffer(seedBuffer(value, placeholder));
    onBlur?.(e);
  };

  return (
    <div className="relative flex items-center w-full">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        pattern="-?[0-9]*[.,]?[0-9]*"
        value={buffer}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(
          "w-full h-9 px-3 text-sm font-mono text-right rounded-input border transition-all outline-none",
          isManual
            ? "bg-amber-50/50 dark:bg-amber-950/20 border-amber-400 dark:border-amber-700/80 text-amber-900 dark:text-amber-100 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
            : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 focus:border-brand-500 focus:ring-1 focus:ring-brand-500",
          suffix ? "pr-12" : "",
          className
        )}
        {...props}
      />
      {suffix && (
        <span className="absolute right-3 text-xs font-semibold text-slate-400 dark:text-slate-500 pointer-events-none select-none">
          {suffix}
        </span>
      )}
    </div>
  );
};
